import { CloudDownloadOutlined, CopyOutlined, CopyTwoTone, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Modal, Typography } from "antd";
import {
    useGetPayoutsPairsExportMutation,
    useGetPayoutsPairsQuery,
    useUpdatePayoutMutation,
} from "api/TenantSlug/Payout/hooks";
import { TransactionReportDetailDrawer } from "components/Drawers/TransactionReportDetailDrawer/TransactionReportDetailDrawer";
import { TransactionReportEditDrawer } from "components/Drawers/TransactionReportEditDrawer/TransactionReportEditDrawer";
import { StatusMultiSelect } from "components/Report/UI/StatusMultiSelect/StatusMultiSelect.tsx";
import { AcceptRejectTableButtons } from "components/UI/AcceptRejectTableButtons/AcceptRejectTableButtons";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { ResponseWithPagination, Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CountryCell } from "components/UI/Table/UI/CountryCell/CountryCell";
import { DateCell } from "components/UI/Table/UI/DateCell/DateCell.tsx";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell.tsx";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { allPayoutResultValues } from "types/constants.ts";
import { PayoutResult, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";
import { formatLongId } from "utils/formatLongId.ts";

import PayoutPairData = App.Data.PayoutPairData;
import GetPayoutResponse = App.Http.Responses.Payout.GetPayoutResponse;
import GetPayoutsPairsResponse = App.Http.Responses.Payout.GetPayoutsPairsResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";

type PayoutPairRow = PayoutPairData & { id: string };

interface SearchParams {
    [`transactionReportDetail-id`]: string;
    [`transactionReportEdit-id`]: string;
}

export const TransactionReportTable = () => {
    const tableId = TableId.TransactionReportTable;
    const { t } = usePaldockTranslation("tables.transaction-report");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const tableAtomInstance = tableAtom<PayoutPairRow>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const permissionUtils = usePermission();
    const canViewForeignPayout = permissionUtils.hasPermission(PermissionEnum.PayoutViewForeign);
    const exportState = useRecoilValue(exportAtom(tableId));
    const requestInclude = canViewForeignPayout
        ? "user,commission,conversionState,conversionState.owner,conversionState.advertiser,conversionState.offer,conversionState.country,conversionState.cascade"
        : "user,commission,conversionState,conversionState.owner,conversionState.offer,conversionState.country";
    const createdAtStr = useCreatedAtFilterValue();
    const [actualStatuses, setActualStatuses] = useState<PayoutResult[]>(allPayoutResultValues);

    const toggleStatuses = (statuses: PayoutResult[]) => {
        setActualStatuses(statuses);
    };

    const showPlanRestrictionModal = () => {
        Modal.info({
            title: tC("plan_restriction.title"),
            content: tC("plan_restriction.message"),
            okText: tC("ok"),
        });
    };

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                result: {
                    in: actualStatuses,
                },
                ...(tableState.filter?.filter ?? {}),
                ...(tableState.filter?.filter?.created_at
                    ? {}
                    : {
                          created_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-created_at",
            include: requestInclude,
        },
    };

    const getPayoutsPairsQuery = useGetPayoutsPairsQuery(request, { enabled: false });

    const getPayoutsPairsExportMutation = useGetPayoutsPairsExportMutation();

    const updatePayoutMutation = useUpdatePayoutMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetPayoutsPairsResponse,
        GetPayoutResponse,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore PayoutPairData has no top-level id (uses first.id)
        PayoutPairData,
        BaseExportRequest
    >(tableId, getPayoutsPairsQuery, undefined, undefined, getPayoutsPairsExportMutation);

    useEffect(() => {
        tableFetchDataUtils.setTableState((prev) => ({
            ...prev,
            page: { ...prev.page, number: 1 },
            filter: {
                filter: {
                    ...(prev.filter?.filter || {}),
                    result: {
                        in: actualStatuses,
                    },
                },
            },
        }));
    }, [actualStatuses]);

    const handleUpdatePayoutMutation = (payoutId: string, result: PayoutResult) => {
        updatePayoutMutation.mutate(
            { payoutId, request: { result: result } },
            {
                onSuccess: () => tableFetchDataUtils.refetchData(),
            }
        );
    };

    const columnConfig: TableColumnType<PayoutPairRow>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("date")} />,
            dataIndex: "first.created_at",
            key: "first.created_at",
            render: (_, pair) => <DateTimeCell value={pair.first.created_at} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
            filterName: "created_at",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "first.conversionState.eid",
            key: "first.conversionState.eid",
            render: (_, pair) => (
                <>
                    {pair.first.conversionState?.eid ? (
                        <ShortenedIdWithCopy id={pair.first.conversionState.eid} />
                    ) : (
                        <EmptyCell />
                    )}
                </>
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
            filterName: "conversionState.eid",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("origin_id")} />,
            dataIndex: "first.origin_eid",
            key: "first.origin_eid",
            render: (_, pair) => (
                <>{pair.first.origin_eid ? <ShortenedIdWithCopy id={pair.first.origin_eid} /> : <EmptyCell />}</>
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
            filterName: "origin_eid",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "first.send_eid",
            key: "first.send_eid",
            render: (_, pair) => (
                <>{pair.first.send_eid ? <ShortenedIdWithCopy id={pair.first.send_eid} /> : <EmptyCell />}</>
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
            filterName: "send_eid",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "first.conversionState.advertiser_custom_id",
            key: "first.conversionState.advertiser_custom_id",
            render: (_, pair) => {
                const advertiserCustomId = pair.first.conversionState?.advertiser_custom_id;
                if (advertiserCustomId && advertiserCustomId !== "") {
                    return <ShortenedIdWithCopy id={advertiserCustomId} />;
                } else {
                    return <EmptyCell />;
                }
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
            filterName: "conversionState.advertiser_custom_id",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("affiliate_cid")} />,
            dataIndex: "first.conversionState.owner_custom_id",
            key: "first.conversionState.owner_custom_id",
            render: (_, pair) => {
                const ownerCustomId = pair.first.conversionState?.owner_custom_id;
                if (ownerCustomId && ownerCustomId !== "") {
                    return <ShortenedIdWithCopy id={ownerCustomId} />;
                } else {
                    return <EmptyCell />;
                }
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            visible: false,
            width: 150,
            filterName: "conversionState.owner_custom_id",
        },
        TableColumn.Entity.Owner<PayoutPairRow>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "first.conversionState.owner.nickname",
            key: "first.conversionState.owner.nickname",
            filterName: "conversionState.owner_id",
            sortName: "conversionState.owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (pair) => pair.first.conversionState?.owner,
        }),
        ...(canViewForeignPayout
            ? [
                  TableColumn.Entity.Advertiser<PayoutPairRow>({
                      title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
                      dataIndex: "first.conversionState.advertiser.nickname",
                      key: "first.conversionState.advertiser.nickname",
                      filterType: FILTER_TYPES.advertiserSelect,
                      filterName: "conversionState.advertiser_id",
                      sortName: "conversionState.advertiser.nickname",
                      ellipsis: true,
                      width: 150,
                      getEntity: (pair) => pair.first.conversionState?.advertiser,
                  }),
              ]
            : []),
        TableColumn.Entity.Offer<PayoutPairRow>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "first.conversionState.offer.title",
            key: "first.conversionState.offer.title",
            sortName: "conversionState.offer.title",
            ellipsis: true,
            width: 150,
            filterName: "conversionState.offer_id",
            getEntity: (pair) => pair.first.conversionState?.offer,
            render: (_: unknown, pair: PayoutPairRow) =>
                pair.first.conversionState?.offer ? (
                    <EntityLinkCell.Offer entity={pair.first.conversionState.offer} />
                ) : (
                    <EmptyCell />
                ),
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("source")} />,
            dataIndex: "first.conversionState.source",
            key: "first.conversionState.source",
            inputType: "text",
            render: (_, pair) => {
                return pair.first.conversionState?.source ?? <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.conversionStateSourceSelect,
            ellipsis: true,
            visible: false,
            width: 150,
            filterName: "conversionState.source",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "first.type",
            key: "first.type",
            inputType: "text",
            render: (_, pair) => {
                return pair.first.type ?? <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.conversionStateTypeSelect,
            ellipsis: true,
            width: 150,
            filterName: "type",
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion")} />,
            dataIndex: "first.conversionState.type",
            key: "first.conversionState.type",
            inputType: "text",
            render: (_, pair) => {
                return pair.first.conversionState?.type ?? <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.conversionStateTypeSelect,
            ellipsis: true,
            visible: false,
            width: 150,
            filterName: "conversionState.type",
        },
        ...(canViewForeignPayout
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("channel")} />,
                      dataIndex: "first.conversionState.cascade.title",
                      key: "first.conversionState.cascade.title",
                      render: (_: unknown, pair: PayoutPairRow) => {
                          const cascade = pair.first.conversionState?.cascade;
                          return cascade ? <EntityLinkCell.Cascade entity={cascade} /> : <EmptyCell />;
                      },
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "conversionState.cascade.title",
                      sorter: true,
                      width: 150,
                      ellipsis: true,
                  },
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("country")} />,
            dataIndex: "first.conversionState.country.iso3",
            key: "first.conversionState.country.iso3",
            inputType: "text",
            editable: true,
            render: (_, pair) => {
                return pair.first.conversionState?.country ? (
                    <CountryCell country={pair.first.conversionState.country} />
                ) : (
                    <EmptyCell />
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.countrySelect,
            filterName: "conversionState.country_id",
            sortName: "conversionState.country.name",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("category")} />,
            dataIndex: "first.category",
            key: "first.category",
            inputType: "text",
            ellipsis: true,
            visible: false,
            width: 150,
            filterName: "category",
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.commission")} />,
            dataIndex: "first.commission.description",
            key: "first.commission.id",
            render: (_: unknown, pair: PayoutPairRow) => {
                const commission = pair.first.commission;
                if (commission?.description) {
                    return <EntityLinkCell.Commission entity={commission} />;
                }
                if (commission) {
                    return (
                        <span className="flex items-center gap-1">
                            <EntityLinkCell.Commission entity={commission}>
                                {formatLongId(commission.id)}
                            </EntityLinkCell.Commission>
                            <Typography.Text
                                copyable={{
                                    text: commission.id,
                                    icon: [<CopyOutlined key="copyOutlined" />, <CopyTwoTone key="copyTwoTone" />],
                                    tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                                }}
                            />
                        </span>
                    );
                }
                return pair.first.commission_id ? <ShortenedIdWithCopy id={pair.first.commission_id} /> : <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            visible: false,
            width: 150,
            filterName: "commission.description",
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.origin_date")} />,
            dataIndex: "first.conversionState.created_at",
            key: "first.conversionState.created_at",
            render: (_, pair) => {
                const createdAt = pair.first.conversionState?.created_at;
                if (createdAt) {
                    return <DateCell value={createdAt} />;
                } else {
                    return <EmptyCell />;
                }
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.date,
            filterName: "conversionState.created_at",
            ellipsis: true,
            visible: false,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.approved_at")} />,
            dataIndex: "first.approved_at",
            key: "first.approved_at",
            render: (_, pair) => <DateCell value={pair.first.approved_at} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.date,
            ellipsis: true,
            width: 150,
            filterName: "approved_at",
        },
        ...(canViewForeignPayout
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.affiliate_commission")} />,
                      dataIndex: "user.amount",
                      key: "user.amount",
                      inputType: "text",
                      render: (_: unknown, pair: PayoutPairRow) => {
                          const userPayout = pair.user;
                          return userPayout ? (
                              <MoneyCell
                                  money={{
                                      amount: userPayout.amount,
                                      currency_id: userPayout.currency_id,
                                      formatted_amount: userPayout.formatted_amount,
                                  }}
                              />
                          ) : (
                              <EmptyCell />
                          );
                      },
                      sorter: false,
                      filterable: false,
                      filterType: FILTER_TYPES.number,
                      //filterName: "firstChild.amount",
                      ellipsis: true,
                      width: 130,
                  },
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.advertiser_commission")} />,
                      dataIndex: "tenant.amount",
                      key: "tenant.amount",
                      inputType: "text",
                      render: (_: unknown, pair: PayoutPairRow) => {
                          const tenantPayout = pair.tenant;
                          return tenantPayout ? (
                              <MoneyCell
                                  money={{
                                      amount: tenantPayout.amount,
                                      currency_id: tenantPayout.currency_id,
                                      formatted_amount: tenantPayout.formatted_amount,
                                  }}
                              />
                          ) : (
                              <EmptyCell />
                          );
                      },
                      sorter: true,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      filterName: "amount",
                      ellipsis: true,
                      width: 130,
                  },
              ]
            : [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("commission")} />,
                      dataIndex: "user.amount",
                      key: "user.amount",
                      inputType: "text",
                      render: (_: unknown, pair: PayoutPairRow) => {
                          const userPayout = pair.user;
                          return userPayout ? (
                              <MoneyCell
                                  money={{
                                      amount: userPayout.amount,
                                      currency_id: userPayout.currency_id,
                                      formatted_amount: userPayout.formatted_amount,
                                  }}
                              />
                          ) : (
                              <EmptyCell />
                          );
                      },
                      sorter: false,
                      filterable: false,
                      filterType: FILTER_TYPES.number,
                      //filterName: "firstChild.amount",
                      ellipsis: true,
                      width: 130,
                  },
              ]),
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "first.result",
            key: "first.result",
            render: (_: unknown, pair: PayoutPairRow) =>
                permissionUtils.hasPermission(PermissionEnum.PayoutUpdate) ? (
                    <AcceptRejectTableButtons
                        isAccepted={pair.first.result === PayoutResult.Approved}
                        isRejected={pair.first.result === PayoutResult.Rejected}
                        onReject={() => handleUpdatePayoutMutation(pair.first.id, PayoutResult.Rejected)}
                        onAccept={() => handleUpdatePayoutMutation(pair.first.id, PayoutResult.Approved)}
                    />
                ) : (
                    (pair.first.result ?? <EmptyCell />)
                ),
            sorter: true,
            ellipsis: true,
            width: 120,
            filterName: "result",
        },
    ];

    const { getRowHandlers, getRowClassName } = useRowHandlers<PayoutPairRow>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.TransactionReportDetail,
                drawerParam: `${DrawerId.TransactionReportDetail}-id`,
                permission: PermissionEnum.AffiliateTransactionView,
                condition: () => !permissionUtils.hasPermission(PermissionEnum.AffiliateTransactionEdit),
            },
            {
                type: "drawer",
                drawerId: DrawerId.TransactionReportEdit,
                drawerParam: `${DrawerId.TransactionReportEdit}-id`,
                permission: PermissionEnum.AffiliateTransactionEdit,
            },
        ],
    });

    return (
        <>
            <Table<ResponseWithPagination, PayoutPairRow>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetPayoutsPairsResponse}
                dataSource={(tableFetchDataUtils.tableData?.payoutPairs || []).map((pair) => ({
                    ...pair,
                    id: pair.first.id,
                }))}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.first.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        filter: request.filter,
                        extension: exportState.extension,
                        headers: exportState.headers,
                        page: request.page,
                        export_all: exportState.range === "all",
                        delimiter: exportState.delimiter,
                    })
                }
                expandable={{
                    childrenColumnName: "dont_show",
                }}
                enableFilters={permissionUtils.hasPermission(PermissionEnum.ReportSelectFilter)}
                enableTimeFilter={permissionUtils.hasPermission(PermissionEnum.ReportSelectFilterTime)}
                dateFilterDataIndex={"created_at"}
                extraTopBarElements={<StatusMultiSelect onChange={toggleStatuses} />}
                extraTopBarIcons={
                    <>
                        {permissionUtils.hasPermission(PermissionEnum.PayoutImport) && (
                            <Button icon={<CloudDownloadOutlined />} onClick={showPlanRestrictionModal}>
                                {tC("import")}
                            </Button>
                        )}
                        {permissionUtils.hasPermission(PermissionEnum.PayoutCreate) && (
                            <Button type="primary" icon={<PlusOutlined />} onClick={showPlanRestrictionModal}>
                                {tC("create")}
                            </Button>
                        )}
                        {permissionUtils.hasPermission(PermissionEnum.PayoutUpdate) && (
                            <Button type="primary" icon={<EditOutlined />} onClick={showPlanRestrictionModal}>
                                {tC("edit")}
                            </Button>
                        )}
                    </>
                }
                enableWordWrap={true}
                autoScrollWidth={true}
            />

            {searchParams[`${DrawerId.TransactionReportDetail}-id`] && (
                <TransactionReportDetailDrawer payoutId={searchParams[`${DrawerId.TransactionReportDetail}-id`]} />
            )}
            {searchParams[`${DrawerId.TransactionReportEdit}-id`] && (
                <TransactionReportEditDrawer
                    onSubmit={() => tableFetchDataUtils.refetchData()}
                    payoutId={searchParams[`${DrawerId.TransactionReportEdit}-id`]}
                />
            )}
        </>
    );
};
